import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  useTheme,
} from "@mui/material";
import { MergeType as MergeIcon } from "@mui/icons-material";
import { tokens } from "../../../theme";
import type { IndividualContactDetails } from "../../../types/contact";

interface ContactMergeDialogProps {
  open: boolean;
  onClose: () => void;
  primaryContact: IndividualContactDetails;
  secondaryContact: IndividualContactDetails | null;
  secondaryLoading: boolean;
  onMerge: (fieldSelections: Record<string, "primary" | "secondary">) => void;
  merging: boolean;
}

interface FieldGroup {
  label: string;
  fields: { key: string; label: string }[];
}

const FIELD_GROUPS: FieldGroup[] = [
  {
    label: "Basic Information",
    fields: [
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "address_one", label: "Address Line 1" },
      { key: "address_two", label: "Address Line 2" },
      { key: "suburb", label: "Suburb" },
      { key: "city", label: "City" },
      { key: "country", label: "Country" },
      { key: "notes", label: "Notes" },
    ],
  },
  {
    label: "Social Profiles",
    fields: [
      { key: "facebook_profile", label: "Facebook" },
      { key: "instagram_profile", label: "Instagram" },
      { key: "twitter_profile", label: "Twitter" },
      { key: "linkedin_profile", label: "LinkedIn" },
      { key: "website", label: "Website" },
    ],
  },
];

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "N/A")
    return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

export const ContactMergeDialog: React.FC<ContactMergeDialogProps> = ({
  open,
  onClose,
  primaryContact,
  secondaryContact,
  secondaryLoading,
  onMerge,
  merging,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [fieldSelections, setFieldSelections] = useState<
    Record<string, "primary" | "secondary">
  >({});
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (secondaryContact) {
      const initialSelections: Record<string, "primary" | "secondary"> = {};
      for (const group of FIELD_GROUPS) {
        for (const field of group.fields) {
          const primaryVal = (primaryContact as unknown as Record<string, unknown>)[
            field.key
          ];
          const secondaryVal = (secondaryContact as unknown as Record<string, unknown>)[
            field.key
          ];
          const primaryEmpty =
            primaryVal === null ||
            primaryVal === undefined ||
            primaryVal === "" ||
            primaryVal === "N/A";
          const secondaryEmpty =
            secondaryVal === null ||
            secondaryVal === undefined ||
            secondaryVal === "" ||
            secondaryVal === "N/A";

          if (primaryEmpty && !secondaryEmpty) {
            initialSelections[field.key] = "secondary";
          } else {
            initialSelections[field.key] = "primary";
          }
        }
      }
      setFieldSelections(initialSelections);
      setConfirmStep(false);
    }
  }, [secondaryContact, primaryContact]);

  const handleSelectionChange = (
    fieldKey: string,
    value: "primary" | "secondary",
  ) => {
    setFieldSelections((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleMerge = () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    onMerge(fieldSelections);
  };

  const handleClose = () => {
    setConfirmStep(false);
    onClose();
  };

  const selectionCounts = useMemo(() => {
    const counts = { primary: 0, secondary: 0 };
    for (const val of Object.values(fieldSelections)) {
      counts[val]++;
    }
    return counts;
  }, [fieldSelections]);

  const primaryName = [primaryContact.first_name, primaryContact.last_name]
    .filter(Boolean)
    .join(" ");
  const secondaryName = secondaryContact
    ? [secondaryContact.first_name, secondaryContact.last_name]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MergeIcon />
          <Typography variant="h5" fontWeight="bold">
            Merge Contacts
          </Typography>
        </Box>
        {secondaryContact && (
          <Box display="flex" gap={2} mt={1}>
            <Chip
              label={`Primary: ${primaryName}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Secondary: ${secondaryName}`}
              color="secondary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {secondaryLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !secondaryContact ? (
          <Typography>Secondary contact not found.</Typography>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              All bookings, documents, and venue associations from{" "}
              <strong>{secondaryName}</strong> will be transferred to{" "}
              <strong>{primaryName}</strong>. The secondary contact will be
              permanently deleted. This action cannot be undone.
            </Alert>

            <Box display="flex" justifyContent="center" gap={2} mb={2}>
              <Typography variant="body2">
                Fields from Primary:{" "}
                <strong>{selectionCounts.primary}</strong>
              </Typography>
              <Typography variant="body2">
                Fields from Secondary:{" "}
                <strong>{selectionCounts.secondary}</strong>
              </Typography>
            </Box>

            {FIELD_GROUPS.map((group) => (
              <Box key={group.label} mb={3}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    backgroundColor: colors.primary[800],
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  {group.label}
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr 1fr"
                  gap={1}
                  px={2}
                  py={1}
                  sx={{ borderBottom: `1px solid ${colors.grey[800]}` }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    Field
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" align="center">
                    Primary Value
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" align="center">
                    Secondary Value
                  </Typography>
                </Box>

                {group.fields.map((field) => {
                  const primaryVal = (
                    primaryContact as unknown as Record<string, unknown>
                  )[field.key];
                  const secondaryVal = (
                    secondaryContact as unknown as Record<string, unknown>
                  )[field.key];
                  const selection = fieldSelections[field.key] || "primary";

                  return (
                    <Box
                      key={field.key}
                      display="grid"
                      gridTemplateColumns="1fr 1fr 1fr"
                      gap={1}
                      px={2}
                      py={0.5}
                      alignItems="center"
                      sx={{
                        "&:hover": { backgroundColor: colors.primary[800] },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {field.label}
                      </Typography>

                      <RadioGroup
                        row
                        value={selection}
                        onChange={(e) =>
                          handleSelectionChange(
                            field.key,
                            e.target.value as "primary" | "secondary",
                          )
                        }
                        sx={{ justifyContent: "center" }}
                      >
                        <FormControlLabel
                          value="primary"
                          control={<Radio size="small" />}
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selection === "primary" ? "bold" : "normal",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatFieldValue(primaryVal)}
                            </Typography>
                          }
                        />
                      </RadioGroup>

                      <RadioGroup
                        row
                        value={selection}
                        onChange={(e) =>
                          handleSelectionChange(
                            field.key,
                            e.target.value as "primary" | "secondary",
                          )
                        }
                        sx={{ justifyContent: "center" }}
                      >
                        <FormControlLabel
                          value="secondary"
                          control={<Radio size="small" />}
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  selection === "secondary" ? "bold" : "normal",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatFieldValue(secondaryVal)}
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Box>
                  );
                })}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}

            {confirmStep && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Are you sure? This will permanently delete &quot;
                  {secondaryName}&quot; and transfer all records to &quot;
                  {primaryName}&quot;.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={merging}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={confirmStep ? "error" : "primary"}
          onClick={handleMerge}
          disabled={merging || secondaryLoading || !secondaryContact}
          startIcon={
            merging ? <CircularProgress size={18} /> : <MergeIcon />
          }
        >
          {merging
            ? "Merging..."
            : confirmStep
              ? "Confirm Merge"
              : "Merge Contacts"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactMergeDialog;
